import { diagnostics } from '@/diagnostics/browserDiagnostics';
import { ProjectSaveConflict } from './ProjectEditSession';
import type { ProjectSaveCommand } from './ProjectSaveWorkerEngine';
import type { CurrentProjectSaveCommand } from './CurrentProjectSaveWorkerEngine';

export interface ProjectSaveWorkerPort {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  terminate(): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
}

/** One persistent worker; response IDs keep queue/session/attempt ownership explicit. */
export class ProjectSaveWorkerClient {
  private nextId = 0;
  private pending = new Map<
    number,
    { resolve(value: unknown): void; reject(error: Error): void }
  >();
  private failure?: Error;
  private readonly worker: ProjectSaveWorkerPort;
  constructor(createWorker: () => ProjectSaveWorkerPort) {
    this.worker = createWorker();
    this.worker.onmessage = ({ data }) => {
      const waiting = this.pending.get(data.id);
      if (!waiting) return;
      this.pending.delete(data.id);
      if (data.error) {
        diagnostics.record('save', 'worker');
        console.error('Project save failed', data.error.stack ?? data.error.message);
        waiting.reject(
          Array.isArray(data.error.chips)
            ? new ProjectSaveConflict(data.error.chips)
            : new Error(data.error.message),
        );
      } else waiting.resolve(data.value);
    };
    this.worker.onerror = () =>
      this.fail(
        new Error(
          'Project save worker stopped. Reload this project before saving again.',
        ),
      );
    this.worker.onmessageerror = () =>
      this.fail(
        new Error(
          'Project save worker message failed. Reload this project before saving again.',
        ),
      );
  }

  get isFailed(): boolean {
    return this.failure !== undefined;
  }

  request<T = void>(
    command: ProjectSaveCommand | CurrentProjectSaveCommand,
    transfer: Transferable[] = [],
  ): Promise<T> {
    if (this.failure) return Promise.reject(this.failure);
    const id = ++this.nextId;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: (value) => resolve(value as T), reject });
      try {
        // Browser structured clone captures input NOW, before the main save FIFO.
        this.worker.postMessage({ id, command }, transfer);
      } catch (error) {
        this.pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private fail(error: Error) {
    diagnostics.record('save', 'worker');
    this.failure = error;
    this.worker.terminate();
    for (const waiting of this.pending.values()) waiting.reject(error);
    this.pending.clear();
  }
}
